import BinCard from "@/components/bin-card";
import SearchBar from "@/components/searchbar";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useBuyCards } from "@/hooks/use-mutations";
import { CountryCodeSelect } from "@/lib/country-code-select";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { binCards } from "./Shop";
import Throbber from "@/components/throbber";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const VirtualCards = () => {
  const [expanded, setExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const bin = searchParams.get("bin");
  const id = searchParams.get("id");
  const [countryCode, setCountryCode] = useState("NG");
  const navigate = useNavigate();
  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      dob: "",
      phone: "",
      postalCode: "",
      initialAmount: "",
    },
  });

  useEffect(() => {
    if (!bin) {
      navigate("/dashboard/shop");
      toast.info("Please select a card type");
    }
  }, [bin]);

  const { mutateAsync: createCard, isPending: isCreating } = useBuyCards({
    onSuccess: (data) => {
      queryClient.invalidateQueries(["cards", "user"]);
      navigate("/dashboard/cards");
    },
  });

  const onSubmit = (data) => {
    const formData = {
      bin: bin,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneCode: countryCode,
      phone: data.phone,
      useType: "Online transactions",
      cost: parseInt(data.initialAmount),
    };
    createCard(formData);
  };
  return (
    <div>
      {/* header */}
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="bg-primary-50 hover:bg-primary-50/90 flex size-8 items-center justify-center rounded-md"
            >
              <ArrowLeft className="size-6" />
            </button>
            <div className="mb-1 ml-2 flex items-center gap-2 text-xl font-medium">
              <span>Virtual Cards</span>
              <span className="text-primary-50 mt-1.5">
                <ChevronRight size={16} />
              </span>
              <span>Shop</span>
            </div>
          </div>

          <p className="text-3xl font-bold">Apply for Cards</p>
        </div>

        <SearchBar />
      </header>

      <section className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <div className="w-80">
            <BinCard showCta={false} card={binCards[0]} />
          </div>
          <form className="mt-4" onSubmit={form.handleSubmit(onSubmit)}>
            <Form {...form}>
              <div className="space-y-2.5">
                <div className="flex items-center gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="John" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Doe" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type={"email"}
                          {...field}
                          placeholder="johndoe@example.com"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center gap-4">
                  <FormField
                    control={form.control}
                    name="dob"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <FormLabel>Date of Birth</FormLabel>
                        <FormControl>
                          <Input
                            type={"date"}
                            {...field}
                            placeholder="DD/MM/YYYY"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0">
                              <CountryCodeSelect
                                className="text-[8px]"
                                onSelect={setCountryCode}
                              />
                            </div>
                            <Input
                              type={"tel"}
                              {...field}
                              className={"pl-16"}
                              placeholder="000 000 000"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="initialAmount"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Deposit Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter the amount you want to credit to your card."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex items-center gap-2.5">
                <FormField
                  control={form.control}
                  name="esign"
                  render={({ field }) => (
                    <FormItem className="mt-2 flex w-1/2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-primary-50 font-normal">
                        I accept the {""}
                        <Link
                          to={"/pdfs/E-sign template.pdf"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-500 font-semibold"
                        >
                          E-Sign Consent
                        </Link>
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cardTerms"
                  render={({ field }) => (
                    <FormItem className="mt-2 flex w-1/2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-primary-50 font-normal">
                        I accept the {""}
                        <Link
                          to={"/pdfs/DigitWallet_Card_Terms.pdf"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-500 font-semibold"
                        >
                          Card Terms
                        </Link>{" "}
                        and {""}
                        <Link
                          to={"/pdfs/DigitWallet card Privacy Policy.pdf"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-500 font-semibold"
                        >
                          Privacy Policy
                        </Link>
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex items-center gap-2.5">
                <FormField
                  control={form.control}
                  name="certify"
                  render={({ field }) => (
                    <FormItem className="mt-2 flex w-1/2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-primary-50 font-normal">
                         I certify that the information I have provided is
                        accurate and that i will abide by all the rules and
                        requirements related to my Zypher Spend Card.
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="spendCard"
                  render={({ field }) => (
                    <FormItem className="mt-2 flex w-1/2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-primary-50 font-normal">
                         I acknowledge that using the Zypher Spend Card does not
                        constitute unauthorized solicitation.
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="lg:hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem value="item-1">
                    <AccordionTrigger
                      className={
                        "flex justify-start text-base leading-3.5 font-medium"
                      }
                    >
                      <span className="inline-block w-3/4">
                        Please kindly read the fees and terms before
                        purchasing the card.
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <h4 className="mt-6 text-lg font-semibold lg:mt-0">
                        Restricted Scenarios
                      </h4>

                      <p className="text-primary-500/50 mt-2 text-lg leading-5">
                        Currently this card will not work at any petrol
                        stations, Uber, Iberia, Ticketmaster, buying insurance,
                        sending money transfers, or anything related to drugs,
                        weapons, or explosives due to visa restrictions
                      </p>
                      <h4 className="mt-4 text-lg font-semibold">
                        Card information{" "}
                      </h4>
                      <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
                        <li>Card Issuer: Visa</li>
                        <li>Card Type: Credit</li>
                        <li>Card Form: Virtual</li>
                        <li>Currency: USD</li>
                        <li>Apple Pay supported: Yes</li>
                        <li>Google Wallet supported: Yes</li>
                      </ol>
                      <h4 className="mt-4 text-lg font-semibold">Fees</h4>
                      <p className="text-primary-500/50 text-lg leading-5">
                        Deposit Fee: FREE
                      </p>
                      <p className="text-primary-500/50 text-lg leading-5">
                        Card Purchase Fee: 25 USD
                      </p>

                      <h4 className="mt-4 text-lg font-semibold">
                        Credit Limits
                      </h4>
                      <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
                        <li>Maximum Deposit: $100,000 USD</li>
                        <li>Maximum Card Purchase Quantity: 10</li>
                        <li>Initial Deposit Required: Yes</li>
                      </ol>
                      <h4 className="mt-4 text-lg font-semibold">
                        Card Usage Notice
                      </h4>
                      <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
                        <li>
                          If the card issuer detects malicious activities, such
                          as bulk refunds, cancellations, failures or
                          chargebacks during card usage, the card will be
                          automatically frozen, and a fee of $50 USD per
                          occurrence will be deducted.
                        </li>
                        <li>
                          If the overall transaction failure rate exceeds 20%,
                          the card will be automatically frozen.
                        </li>
                        <li>
                          If the card has insufficient balance and accumulates
                          up to 3 consecutive authorization failures, the card
                          will be automatically cancelled.
                        </li>
                        <li>
                          If the card is frozen, please contact customer service
                          to apply for unfreezing.
                        </li>
                        <li>The card is valid for 1 years.</li>
                      </ol>
                      <p className="text-primary-500/50 mt-4 text-lg leading-5">
                        <span className="text-primary-500 font-medium">
                          Card Currency:
                        </span>{" "}
                        Card currency refers to the currency type or unit
                        associated with infinity cards. it plays a crucial role
                        in determining the currency used for payment and
                        settlement when utilising Zypher cards. It is highly
                        recommended to utilise cards that align with the
                        currency of the purchase order to mitigate the potential
                        incurring of FX fees.
                      </p>
                      <p className="text-primary-500/50 mt-2 text-lg leading-5">
                        For customers with monthly transaction volumes exceeding
                        500,000 USD, please contact the support team for fee
                        adjustments.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <div className="mt-4 flex items-start gap-4">
                <Button
                  onClick={() => navigate(-1)}
                  className={"grow"}
                  variant={"outline"}
                  type="button"
                  disabled={isCreating}
                >
                  Go Back
                </Button>
                <div className="flex grow flex-col">
                  <Button
                    disabled={
                      isCreating ||
                      !form.watch("certify") ||
                      !form.watch("cardTerms") ||
                      !form.watch("esign") ||
                      !form.watch("spendCard")
                    }
                    className={
                      "disabled:bg-primary-500/50 disabled:cursor-not-allowed"
                    }
                    type="submit"
                  >
                    {isCreating ? <Throbber /> : "Buy Card"}
                  </Button>
                  <span className="text-primary-red text-sm font-semibold">
                    Card Purchase Fee : $25
                  </span>
                </div>
              </div>
            </Form>
          </form>
        </div>

        <div className="hidden lg:block">
          <h4 className="mt-6 text-lg font-semibold lg:mt-0">
            Restricted Scenarios
          </h4>

          <p className="text-primary-500/50 mt-2 text-lg leading-5">
            Currently this card will not work at any petrol stations, Uber,
            Iberia, Ticketmaster, buying insurance, sending money transfers, or
            anything related to drugs, weapons, or explosives due to
            visa restrictions
          </p>
          <h4 className="mt-4 text-lg font-semibold">Card information </h4>
          <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
            <li>Card Issuer: Visa</li>
            <li>Card Type: Credit</li>
            <li>Card Form: Virtual</li>
            <li>Currency: USD</li>
            <li>Apple Pay supported: Yes</li>
            <li>Google Wallet supported: Yes</li>
          </ol>
          <h4 className="mt-4 text-lg font-semibold">Fees</h4>
          <p className="text-primary-500/50 text-lg leading-5">
            Deposit Fee: FREE
          </p>
          <p className="text-primary-500/50 text-lg leading-5">
            Card Purchase Fee: 25 USD
          </p>
          <p className="text-primary-500/50 text-lg leading-5">
            FX Fee: This can vary and only applies if a crossborder transaction
            or the billing currency is different from the settlement currency.
          </p>

          <h4 className="mt-4 text-lg font-semibold">Credit Limits</h4>
          <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
            <li>Maximum Deposit: $100,000 USD</li>
            <li>Maximum Card Purchase Quantity: 10</li>
            <li>Initial Deposit Required: Yes</li>
          </ol>
          <h4 className="mt-4 text-lg font-semibold">Card Usage Notice</h4>
          <ol className="text-primary-500/40 list-decimal pl-8 text-lg leading-4">
            <li>
              If the card issuer detects malicious activities, such as bulk
              refunds, cancellations, failures or chargebacks during card usage,
              the card will be automatically frozen, and a fee of $50 USD per
              occurrence will be deducted.
            </li>
            <li>
              If the overall transaction failure rate exceeds 20%, the card will
              be automatically frozen.
            </li>
            <li>
              If the card has insufficient balance and accumulates up to 3
              consecutive authorization failures, the card will be automatically
              cancelled.
            </li>
            <li>
              If the card is frozen, please contact customer service to apply
              for unfreezing.
            </li>
            <li>The card is valid for 1 years.</li>
          </ol>
          <p className="text-primary-500/50 mt-4 text-lg leading-5">
            <span className="text-primary-500 font-medium">Card Currency:</span>{" "}
            Card currency refers to the currency type or unit associated with
            infinity cards. it plays a crucial role in determining the currency
            used for payment and settlement when utilising Zypher cards. It is
            highly recommended to utilise cards that align with the currency of
            the purchase order to mitigate the potential incurring of FX fees.
          </p>
          <p className="text-primary-500/50 mt-2 text-lg leading-5">
            For customers with monthly transaction volumes exceeding 500,000
            USD, please contact the support team for fee adjustments.
          </p>
        </div>
      </section>
    </div>
  );
};

export default VirtualCards;
